import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './loader.css';

interface LoaderProps {
  /** Adds additional classes to the component. */
  className?: string;
  /** General description of what is being loaded */
  title?: string;
  /** Specific information about what the system is doing */
  description?: string;
  /** Hide or show the loader with transitions */
  visible?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * Loader Component
 *
 * @param {LoaderProps} param
 *
 * @returns JSX.ELEMENT
 */
export const Loader = ({
  className,
  title,
  description,
  'data-testid': dataTestid,
  visible = true
}: LoaderProps) => {
  /**
   * This is a bit of a hack to be able to get the loader to animate in and out smoothly,
   * before being completely removed from the page with display: none. It works by delaying
   * when the `display: none` property is added until after the css animation completes.
   */
  const [displayed, setDisplayed] = useState(false);
  const [delayedVisible, setDelayedVisible] = useState(false);

  useEffect(() => {
    setDelayedVisible(visible);
    const timer = setTimeout(() => setDisplayed(visible), 200);

    // cleanup function
    return () => clearTimeout(timer);
  }, [visible]);

  const containerClasses = classnames(styles.container, className, {
    [styles.hidden]: !displayed && !visible,
    [styles.hiddenStart]: !delayedVisible
  });

  return (
    <div className={containerClasses} data-testid={dataTestid}>
      <div className={styles.loader}>
        <div className={styles.child} />
        <div className={`${styles.child} ${styles.bounce2}`} />
      </div>
      {title && <div className={styles.title}>{title}</div>}
      {description && description !== '' && <div className={styles.description}>{description}</div>}
    </div>
  );
};

export default Loader;
